import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "../utils/api";

const DEFAULT_BRANDING = {
    slug: "primary",
    displayName: "Traveego",
    brandName: "Traveego",
    logoUrl: "/traveego-logo.jpeg",
};

const TenantContext = createContext(null);

export const useTenant = () => {
    const context = useContext(TenantContext);
    if (!context) {
        throw new Error("useTenant must be used within a TenantProvider");
    }
    return context;
};

export const TenantProvider = ({ children }) => {
    const [tenant, setTenant] = useState(() => {
        try {
            const cached = localStorage.getItem("tenant_branding");
            if (cached) {
                return JSON.parse(cached);
            }
        } catch (_) {}
        return DEFAULT_BRANDING;
    });
    const [loading, setLoading] = useState(true);

    const applyBranding = useCallback((data) => {
        const brandName = data.brand_name || data.brandName || data.display_name || data.displayName || DEFAULT_BRANDING.brandName;
        const logoUrl = data.logo_url || data.logoUrl || DEFAULT_BRANDING.logoUrl;
        const displayName = data.display_name || data.displayName || brandName;
        const slug = data.slug || DEFAULT_BRANDING.slug;

        const updated = {
            slug,
            displayName,
            brandName,
            logoUrl,
        };

        setTenant(updated);
        try {
            localStorage.setItem("tenant_branding", JSON.stringify(updated));
        } catch (_) {}

        if (typeof document !== "undefined") {
            document.title = brandName;
        }
        return updated;
    }, []);

    const fetchTenantInfo = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.getTenantPublicInfo();
            if (response.data) {
                applyBranding(response.data);
            }
        } catch (err) {
            console.warn("Could not fetch tenant public branding, using default/cached:", err);
        } finally {
            setLoading(false);
        }
    }, [applyBranding]);

    useEffect(() => {
        fetchTenantInfo();
    }, [fetchTenantInfo]);

    const updateBranding = useCallback((newBranding) => {
        return applyBranding(newBranding);
    }, [applyBranding]);

    const value = {
        tenant,
        brandName: tenant.brandName,
        logoUrl: tenant.logoUrl,
        displayName: tenant.displayName,
        slug: tenant.slug,
        loading,
        refreshTenant: fetchTenantInfo,
        updateBranding,
    };

    return (
        <TenantContext.Provider value={value}>
            {children}
        </TenantContext.Provider>
    );
};
